import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Button } from "primereact/button";

import AppBreadcrumb from "../../components/CustomeBreadcrumb/AppBreadcrumb";
import { getSpecificDetails, postDataByID } from "../../service/ApiService";
import { GET_UBER_CALCULATION, UBER_CALCULATION_PDF } from "../../config/ApiConstant";
import GstSummaryCards from "./GstSummaryCards";

const fmt = (n: any) =>
  `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const UberCalcDetail = () => {
  const { id } = useParams<{ id: string }>();
  const decodedId = id ? atob(id) : null;
  const navigate = useNavigate();
  const toast = useRef<any>(null);

  const [data, setData] = useState<any>(null);
  const [pageLoad, setPageLoad] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);

  const handlePrint = async () => {
    setPrintLoading(true);
    try {
      const res = await postDataByID(UBER_CALCULATION_PDF, decodedId);
      if (res?.data) {
        window.open(res.data, "_blank");
      } else {
        toast.current?.show({ severity: "error", summary: "Error", detail: "PDF URL not found" });
      }
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Error", detail: error?.response?.data?.message || "Failed to generate PDF" });
    } finally {
      setPrintLoading(false);
    }
  };

  document.title = "GST Calc Detail | Nagra Accounting";

  useEffect(() => {
    fetchDetail();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const fetchDetail = async () => {
    try {
      const res = await getSpecificDetails(GET_UBER_CALCULATION, decodedId);
      if (res) {
        if (typeof res.income_data === "string") {
          try { res.income_data = JSON.parse(res.income_data); } catch { res.income_data = []; }
        }
        if (typeof res.expense_data === "string") {
          try { res.expense_data = JSON.parse(res.expense_data); } catch { res.expense_data = []; }
        }
        if (!Array.isArray(res.income_data)) res.income_data = [];
        if (!Array.isArray(res.expense_data)) res.expense_data = [];
      }
      setData(res);
      setPageLoad(true);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message || "Failed to load record",
      });
      setPageLoad(true);
    }
  };

  if (!pageLoad) {
    return (
      <>
        <AppBreadcrumb currentMenu="GST Calc" pageName="View GST Calc" pageTitle="View GST Calc" />
        <div className="flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }} />
        </div>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <AppBreadcrumb currentMenu="GST Calc" pageName="View GST Calc" pageTitle="View GST Calc" />
        <Card className="bg-white">
          <p className="text-center text-500">Record not found.</p>
        </Card>
      </>
    );
  }

  return (
    <>
      <AppBreadcrumb currentMenu="GST Calc" pageName="View GST Calc" pageTitle="View GST Calc" goToPage={true} />
      <Toast ref={toast} />

      <div className="flex justify-content-end mb-2 gap-2">
        <Button
          label="Edit"
          icon="pi pi-pencil"
          className="common-edit-btn"
          disabled={data?.status === "Done"}
          onClick={() => navigate(`/gst-calc/edit/${id}`)}
        />
        <Button
          label="Print"
          icon="pi pi-print"
          className="common-primary-button"
          loading={printLoading}
          onClick={handlePrint}
        />
      </div>

      <div className="grid">
        <div className="col-12">


          {/* ── Basic Info ── */}
          <Card className="mb-3 section-container">
            <div className="grid">
              {data.name && <InfoCell label="Name" value={data.name} />}

              {data.sin_number && <InfoCell label="SIN Number" value={data.sin_number} />}

              {data.mobile_no && <InfoCell label="Mobile No" value={data.mobile_no} />}

              {data.gst_no && <InfoCell label="GST Number" value={data.gst_no} />}

              {data.access_code && <InfoCell label="Access Code" value={data.access_code} />}


              {data.year && <InfoCell label="Year" value={data.year} />}

              {data.gst_percent && (
                <InfoCell label="GST %" value={`${data.gst_percent}%`} />
              )}

            </div>
          </Card>

          {/* ── Income Breakdown ── */}
          <Card className="mb-3 section-container">
            <h5 className="mt-0 mb-3" style={{ color: "#2c3e50" }}>Income Breakdown</h5>
            <div className="overflow-x-auto">
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
                <thead>
                  <tr style={{ background: "#343a40", color: "#fff" }}>
                    <th style={thS}>#</th>
                    <th style={thS}>Source Type</th>
                    <th style={{ ...thS, textAlign: "right" }}>Gross</th>
                    <th style={{ ...thS, textAlign: "right" }}>Tips</th>
                    <th style={{ ...thS, textAlign: "right" }}>Service Fees</th>
                    <th style={{ ...thS, textAlign: "center" }}>Has GST</th>
                    <th style={{ ...thS, textAlign: "right" }}>Net Income</th>
                    <th style={{ ...thS, textAlign: "right" }}>GST</th>
                    <th style={{ ...thS, textAlign: "right" }}>Taxable Income</th>
                  </tr>
                </thead>
                <tbody>
                  {data.income_data?.map((row: any, i: number) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f8f9fa" }}>
                      <td style={tdS}>{i + 1}</td>
                      <td style={tdS}>{row.source_type}</td>
                      <td style={{ ...tdS, textAlign: "right" }}>{fmt(row.gross)}</td>
                      <td style={{ ...tdS, textAlign: "right" }}>{fmt(row.tips)}</td>
                      <td style={{ ...tdS, textAlign: "right" }}>{fmt(row.service_fees)}</td>
                      <td style={{ ...tdS, textAlign: "center" }}>
                        <i
                          className={`pi ${row.has_gst ? "pi-check-circle" : "pi-times-circle"}`}
                          style={{ color: row.has_gst ? "#28a745" : "#dc3545" }}
                        />
                      </td>
                      <td style={{ ...tdS, textAlign: "right" }}>{fmt(row.net_after_fees)}</td>
                      <td style={{ ...tdS, textAlign: "right", color: row.gst > 0 ? "#dc3545" : undefined }}>
                        {fmt(row.gst)}
                      </td>
                      <td style={{ ...tdS, textAlign: "right", fontWeight: 600 }}>
                        {fmt(row.taxable_income)}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background: "#e9ecef", fontWeight: 700 }}>
                    <td style={tdS} colSpan={2}>Total</td>
                    <td style={{ ...tdS, textAlign: "right" }}>{fmt(data.total_gross_income)}</td>
                    <td style={{ ...tdS, textAlign: "right" }}>{fmt(data.total_tips)}</td>
                    <td style={{ ...tdS, textAlign: "right" }}>{fmt(data.total_service_fees)}</td>
                    <td style={tdS}></td>
                    <td style={{ ...tdS, textAlign: "right" }}>{fmt(data.total_net_after_fees)}</td>
                    <td style={{ ...tdS, textAlign: "right" }}>{fmt(data.total_gst_collected)}</td>
                    <td style={{ ...tdS, textAlign: "right" }}>{fmt(data.total_taxable_income)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>

          {/* ── Expense Breakdown ── */}
          {data.expense_data?.length > 0 &&
            <Card className="mb-3  section-container">
              <h5 className="mt-0 mb-3" style={{ color: "#2c3e50" }}>Expense Breakdown</h5>
              <div className="overflow-x-auto mb-4">
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
                  <thead>
                    <tr style={{ background: "#343a40", color: "#fff" }}>
                      <th style={thS}>#</th>
                      <th style={thS}>Description</th>
                      <th style={{ ...thS, textAlign: "right" }}>Gross</th>
                      <th style={{ ...thS, textAlign: "right" }}>GST (ITC)</th>
                      <th style={{ ...thS, textAlign: "right" }}>Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.expense_data?.map((row: any, i: number) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f8f9fa" }}>
                        <td style={tdS}>{i + 1}</td>
                        <td style={tdS}>{row.description}</td>
                        <td style={{ ...tdS, textAlign: "right" }}>{fmt(row.gross)}</td>
                        <td style={{ ...tdS, textAlign: "right", color: "#28a745" }}>{fmt(row.gst)}</td>
                        <td style={{ ...tdS, textAlign: "right", fontWeight: 600 }}>{fmt(row.net)}</td>
                      </tr>
                    ))}
                    <tr style={{ background: "#e9ecef", fontWeight: 700 }}>
                      <td style={tdS} colSpan={2}>Total</td>
                      <td style={{ ...tdS, textAlign: "right" }}>{fmt(data.total_expense_gross)}</td>
                      <td style={{ ...tdS, textAlign: "right" }}>{fmt(data.total_expense_gst)}</td>
                      <td style={{ ...tdS, textAlign: "right" }}>{fmt(data.total_expense_net)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mb-3 flex gap-4">
                {data.car_gst != null && (
                  <div><span style={{ fontWeight: 600 }}>Vehicle GST: </span>{fmt(data.car_gst)}</div>
                )}
                {data.insurance_amount != null && (
                  <div><span style={{ fontWeight: 600 }}>Insurance Amount: </span>{fmt(data.insurance_amount)}</div>
                )}
              </div>
            </Card>}

          {/* ── General GST + Simplified GST ── */}
          <GstSummaryCards data={data} cardClassName="mb-3 h-full section-container" totalIncomeLabel="Total Income" />

        </div>
      </div>
    </>
  );
};

// ─── Small reusable components ────────────────────────────────────────────────

const InfoCell = ({ label, value }: { label: string; value: string }) => (
  <div className="col-12 md:col-3 sm:col-6">
    <div style={{ fontSize: "0.72rem", color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "2px" }}>
      {label}
    </div>
    <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "#2c3e50" }}>{value}</div>
  </div>
);


// ─── Styles ───────────────────────────────────────────────────────────────────

const thS: React.CSSProperties = {
  padding: "10px 14px",
  textAlign: "left",
  fontWeight: 600,
  fontSize: "0.8rem",
  whiteSpace: "nowrap",
  letterSpacing: "0.02em",
};

const tdS: React.CSSProperties = {
  padding: "9px 14px",
  borderBottom: "1px solid #dee2e6",
  fontSize: "0.875rem",
};

export default UberCalcDetail;
